import { v4 as uuidv4 } from 'uuid';
import { Page } from '../types';

import initPersistor, { Persistor } from './persistor';
import { ContextStore, ReferrerData } from './types';

export type PageCorrelationData = { page: Page; correlationId: string; location: string };

const getPageLocation = () => `${window.location.origin}${window.location.pathname}${window.location.hash}`;

export const initContextStore = (
  {
    organisationIdPersistor = initPersistor({ storageKey: 'organisation-id' }),
    sessionIdPersistor = initPersistor({ storageKey: 'session-id', ttlSeconds: 30 * 60 }),
    referrerPersistor = initPersistor({ storageKey: 'referrer', ttlSeconds: 30 }),
    initialSessionIdPersistor = initPersistor({ storageKey: 'initial-session-id' }),
  }: {
    organisationIdPersistor?: Persistor<string>;
    sessionIdPersistor?: Persistor<string>;
    referrerPersistor?: Persistor<ReferrerData>;
    initialSessionIdPersistor?: Persistor<string>;
  } = {},
  initialPage?: PageCorrelationData,
): ContextStore => {
  let currentPage: PageCorrelationData | undefined = initialPage;

  const getSessionId = () => {
    let value = sessionIdPersistor.get();
    if (!value) {
      value = uuidv4();
      sessionIdPersistor.set(value);
      initialSessionIdPersistor.get() || initialSessionIdPersistor.set(value);
    }
    return value;
  };

  const getReferrerPageData = (): Pick<ReferrerData, 'page'> | undefined => {
    if (!currentPage) return undefined;
    const { category, type, id } = currentPage.page;
    return { page: { category, type, id, correlationId: currentPage.correlationId } };
  };

  return {
    setCurrentPage: page => {
      currentPage = {
        page,
        correlationId: uuidv4(),
        location: getPageLocation(),
      };
    },
    setReferrer: referrer => {
      referrerPersistor.set({
        ...referrer,
        ...getReferrerPageData(),
      });
    },
    getReferrer: () => referrerPersistor.get(false) || getReferrerPageData(),
    resetReferrer: () => {
      referrerPersistor.set(undefined);
    },
    setOrganisationId: (id: string | undefined) => {
      organisationIdPersistor.set(id);
    },
    getContext: () => {
      const isCurrentPage = getPageLocation() === currentPage?.location;
      const sessionId = getSessionId();
      return {
        sessionId,
        organisationId: organisationIdPersistor.get(),
        pageCorrelationId: isCurrentPage ? currentPage?.correlationId : undefined,
        isReturningUser: initialSessionIdPersistor.get() !== sessionId,
      };
    },
  };
};
